import React, { useState, useEffect, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  IconButton,
  Tooltip,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { Delete, Visibility, Edit } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getToken, base_url } from "../../config/config";

const ViewBestallungs = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const theme = createTheme({
    palette: {
      primary: {
        main: "#1e40af",
      },
      secondary: {
        main: "#64748b",
      },
    },
    components: {
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-head": {
              backgroundColor: "#f8fafc",
              fontWeight: 600,
            },
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-root": {
              padding: "12px 16px",
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: "#f1f5f9 !important",
            },
          },
        },
      },
    },
  });

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await fetch(
          `${base_url}/v1/bestellung/getAllBestallung`,
        );
        if (!response.ok) {
          throw new Error("Failed to fetch orders");
        }
        const data = await response.json();
        setOrders(data.bestellung || []);
        setLoading(false);
      } catch (error) {
        console.error("Fetch error:", error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorFn: (row) => row.firmaId.bezeichnung,
        header: "Company Name",
        size: 200,
        Cell: ({ cell }) => (
          <div className='font-medium'>{cell.getValue()}</div>
        ),
      },
      {
        accessorFn: (row) => row.firmaId.konto,
        header: "Account",
        size: 120,
      },
      {
        accessorFn: (row) => row.firmaId.ustIdNummer,
        header: "VAT Number",
        size: 150,
      },
      {
        accessorFn: (row) => row.details.length,
        header: "Items",
        size: 100,
        Cell: ({ cell }) => (
          <div className='text-center bg-blue-100 text-blue-800 rounded-full px-2 py-1 text-sm font-medium'>
            {cell.getValue()}
          </div>
        ),
      },
    ],
    [],
  );

  const renderDetailPanel = ({ row }) => {
    const order = row.original;
    return (
      <Box
        sx={{
          display: "grid",
          gap: "1rem",
          padding: "1.5rem",
          backgroundColor: "#f8fafc",
          borderRadius: "0.5rem",
          margin: "0.5rem",
        }}>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {order.details.map((detail) => (
            <div
              key={detail._id}
              className='bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200'>
              <div className='space-y-3'>
                <div className='flex items-center'>
                  <span className='font-medium text-gray-700 min-w-32'>
                    Description:
                  </span>
                  <span className='text-gray-800'>{detail.description}</span>
                </div>
                <div className='flex items-center'>
                  <span className='font-medium text-gray-700 min-w-32'>
                    Article Number:
                  </span>
                  <span className='text-gray-800 font-mono'>
                    {detail.artikelNumber}
                  </span>
                </div>
                <div className='flex items-center'>
                  <span className='font-medium text-gray-700 min-w-32'>
                    Quantity:
                  </span>
                  <span className='text-gray-800'>
                    {detail.menge} {detail.unit}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Box>
    );
  };

  if (error) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-xl text-red-600'>Error: {error}</div>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div className='p-6'>
        <h1 className='text-2xl font-bold text-gray-800 mb-6'>
          Orders Management
        </h1>
        <div className='bg-white rounded-xl shadow-lg'>
          <MaterialReactTable
            columns={columns}
            data={orders}
            enableRowActions
            positionActionsColumn='last'
            renderRowActions={({ row }) => (
              <Box sx={{ display: "flex", gap: "0.5rem" }}>
                <Tooltip title='View Details'>
                  <IconButton
                    color='info'
                    onClick={() =>
                      navigate(`/BestellungDetails/${row.original._id}`)
                    }>
                    <Visibility />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Edit'>
                  <IconButton
                    color='primary'
                    onClick={() =>
                      navigate(`/BestellungDetails/${row.original._id}`)
                    }>
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Delete'>
                  <IconButton
                    color='error'
                    onClick={() =>
                      console.log("Delete order:", row.original._id)
                    }>
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            renderDetailPanel={renderDetailPanel}
            enableColumnResizing
            enableFullScreenToggle
            enableDensityToggle
            enableColumnFilters
            enableFilters
            enableHiding
            enablePagination
            enableSorting
            muiTableProps={{
              sx: {
                tableLayout: "fixed",
                "& .MuiTableCell-root": {
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              },
            }}
            state={{
              isLoading: loading,
            }}
            initialState={{
              density: "comfortable",
              pagination: { pageSize: 10, pageIndex: 0 },
              sorting: [{ id: "firmaId.bezeichnung", desc: false }],
            }}
            muiTableHeadCellProps={{
              sx: {
                fontWeight: "bold",
                fontSize: "0.875rem",
              },
            }}
            muiSearchTextFieldProps={{
              sx: { minWidth: "300px" },
              variant: "outlined",
              size: "small",
              placeholder: "Search orders...",
            }}
          />
        </div>
      </div>
    </ThemeProvider>
  );
};

export default ViewBestallungs;
